import type { BusinessFunction } from '@lib/api-types/business-functions';
import type { Engineer } from '@lib/api-types/engineers';
import type { ActivityView } from '@lib/api-types/views';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import { fetchJson } from '../../api.ts';
import {
  applySalary,
  composeLiveAccountingView,
  type LiveAccountingBase,
} from '../views/accounting/compose-live';
import { composeMockAccountingView } from '../views/accounting/mock-data';
import { useAverageSalary } from '../views/accounting/salary-store';
import type { AccountingView } from '../views/accounting/types';

import { useViewQueryState } from './useViews';

const DAY = 24 * 60 * 60;
const PERIOD_DAYS = 30;
/** /views/activity caps `limit` at 200; walk pages until past the window. */
const ACTIVITY_PAGE = 200;
/** Backstop so a very busy org can't page forever. */
const ACTIVITY_SAFETY_MAX = 2000;

type AccountingBase =
  | { kind: 'mock'; view: AccountingView }
  | { kind: 'live'; base: LiveAccountingBase };

/**
 * Pull the org's recent commit feed back far enough to cover the last
 * `PERIOD_DAYS` of *activity*. Items come back committed_at-descending,
 * so the first item is the newest commit; the window is anchored to it
 * (not wall-clock now) because the ingested history is usually a
 * backfill that ends months ago — a now-relative cutoff would page past
 * everything and the view would render blank. We stop once the oldest
 * item on a page predates `newest − PERIOD_DAYS`. Business functions
 * ride along on the activity payload (same catalog the heatmap cells
 * reference), so no separate fetch is needed for them.
 */
async function fetchAccountingBase(slug: string, now: number): Promise<LiveAccountingBase> {
  const activityItems: ActivityView['items'] = [];
  let businessFunctions: BusinessFunction[] = [];
  let cutoff: number | null = null;
  let offset = 0;

  for (;;) {
    const { view } = await fetchJson<{ view: ActivityView }>(
      `/v1/orgs/${slug}/views/activity?limit=${ACTIVITY_PAGE}&offset=${offset}`,
    );
    if (offset === 0) {
      businessFunctions = view.business_functions;
      // Anchor to the newest commit (first item, descending order).
      if (view.items.length > 0) {
        cutoff = view.items[0]!.committed_at - PERIOD_DAYS * DAY;
      }
    }
    activityItems.push(...view.items);
    if (view.items.length === 0 || !view.has_more) break;
    const oldest = view.items[view.items.length - 1]!.committed_at;
    if (cutoff !== null && oldest < cutoff) break; // paged past the window
    offset += view.items.length;
    if (offset >= ACTIVITY_SAFETY_MAX) break;
  }

  const engineers = await fetchJson<{ data: Engineer[] }>(
    `/v1/orgs/${slug}/engineers`,
  );

  return composeLiveAccountingView({
    activityItems,
    engineers: engineers.data,
    businessFunctions,
    now,
    periodDays: PERIOD_DAYS,
  });
}

/**
 * Accounting view hook. Mirrors `useResilienceView` etc.: mock data
 * when signed-out or VITE_USE_MOCK_DATA=true; otherwise composes a
 * live view from the org's real commit feed, engineers, and business
 * functions.
 *
 * The expensive part (fetch + classify) is cached per-org. The average
 * salary is applied in a `useMemo` over that cached base, so adjusting
 * the salary control recomputes costs instantly without refetching.
 */
export function useAccountingView(): UseQueryResult<AccountingView, Error> {
  const { slug, useMock } = useViewQueryState();
  const salaryUsd = useAverageSalary();

  const query = useQuery<AccountingBase, Error>({
    queryKey: ['view', 'accounting', useMock ? 'mock' : (slug ?? 'mock')],
    queryFn: async () => {
      if (useMock || slug === null) {
        return { kind: 'mock', view: composeMockAccountingView() };
      }
      const base = await fetchAccountingBase(slug, Math.floor(Date.now() / 1000));
      return { kind: 'live', base };
    },
    staleTime: 30_000,
  });

  const data = useMemo<AccountingView | undefined>(() => {
    const d = query.data;
    if (!d) return undefined;
    return d.kind === 'mock' ? d.view : applySalary(d.base, salaryUsd);
  }, [query.data, salaryUsd]);

  // `data` is re-derived from the cached base; the rest of the query
  // result (status flags, refetch, etc.) is passed through untouched.
  // Double-cast because `refetch` still returns the base shape.
  return { ...query, data } as unknown as UseQueryResult<AccountingView, Error>;
}
